/**
 * Outreach + mailbox API client.
 * BFF: every call rides the session cookie via fetchWithAuth — no tokens here.
 */
import { fetchWithAuth } from "../utils/authFetch";

const apiBase = () => process.env.REACT_APP_API_URL || "";

async function asJson(res) {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.details = json.details;
    throw err;
  }
  return json;
}

/** Whether the user has a Gmail/Outlook mailbox connected. */
export async function getMailStatus() {
  return asJson(await fetchWithAuth("/api/mail/status"));
}

/**
 * Begin connecting a mailbox. This is a full-page redirect — the server
 * bounces the browser through Auth0 and back to /outreach.
 * @param {"google"|"microsoft"} provider
 */
export function startConnectMailbox(provider) {
  window.location.href = `${apiBase()}/api/mail/connect/${provider}/start`;
}

/** Disconnect the currently linked mailbox. */
export async function unlinkMailbox() {
  return asJson(await fetchWithAuth("/api/mail/unlink", { method: "POST" }));
}

/** Send an email from the user's connected mailbox.
 * - `html`: when true, `body` is treated as HTML (provider Content-Type flips).
 * - `attachments`: array of { name, contentType, contentBase64 }. Total size
 *   capped at 20MB on the server. */
export async function sendOutreachEmail({
  to,
  cc,
  bcc,
  subject,
  body,
  html = false,
  attachments = [],
  // Set by "Send all" so the run shows as one activity entry; absent on single sends.
  batchId,
  batchTotal,
}) {
  return asJson(
    await fetchWithAuth("/api/outreach/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to, cc, bcc, subject, body, html, attachments, batchId, batchTotal }),
    }),
  );
}

/** Paginated sent-email history for the current user. */
export async function getOutreachHistory({ page = 1, pageSize = 20 } = {}) {
  return asJson(
    await fetchWithAuth(
      `/api/outreach/history?page=${page}&pageSize=${pageSize}`,
    ),
  );
}

/** Ask the AI to draft a subject + body. Returns { subject, body }.
 * `signal` lets the caller abort an in-flight draft (Cancel button in the
 * composer). The server may still complete + bill the request, but the
 * client stops waiting and discards the response. */
export async function generateEmailDraft(payload, { signal } = {}) {
  return asJson(
    await fetchWithAuth("/api/ai/email-template", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    }),
  );
}

/**
 * Delete a candidate from the workspace: their pipeline status, every job link,
 * and the record itself where we own it (internal adds). Sourced candidates
 * live in a shared corpus, so only this user's shortlist row is removed —
 * the server decides that from `origin`.
 */
export async function deleteOutreachCandidate(candidateId, origin) {
  const res = await fetchWithAuth(
    `/api/outreach/candidates/${encodeURIComponent(candidateId)}?origin=${
      origin === "external" ? "external" : "internal"
    }`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`candidate DELETE ${res.status}`);
  return res.json();
}
